import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Building2, Check, ChevronDown, Globe, Search } from 'lucide-react'
import { useOpCo } from '../context/OpCoContext'
import { ALL_OPCOS_CODE } from '../config/opcoWorkspace'

// Header OpCo switcher. Hidden with the OpCo flag off, and for users with a
// single OpCo (they see it in the user menu instead). Switching is a full
// page load into /o/<code>/ via switchTo.

const ALL_ENTRY = { code: ALL_OPCOS_CODE, name: 'All OpCos', all: true }

export function shouldShowSwitcher({ enabled, opcos, isOpcoAdmin }) {
  if (!enabled) return false
  return (opcos?.length || 0) > 1 || Boolean(isOpcoAdmin)
}

function confirmSwitch() {
  // Pages with unsaved edits may set window.__xtractDirty.
  if (typeof window !== 'undefined' && window.__xtractDirty) {
    return window.confirm('You may have unsaved changes. Switch workspace?')
  }
  return true
}

export default function WorkspaceSwitcher() {
  const { enabled, opco, opcos, isOpcoAdmin, allOpcos, workspaceCode, switchTo } = useOpCo()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef(null)
  const btnRef = useRef(null)
  const inputRef = useRef(null)
  const listId = useId()

  const entries = useMemo(() => {
    const list = isOpcoAdmin ? [ALL_ENTRY, ...(opcos || [])] : [...(opcos || [])]
    const q = query.trim().toLowerCase()
    if (!q) return list
    return list.filter((o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q))
  }, [opcos, isOpcoAdmin, query])

  const currentCode = (allOpcos ? ALL_OPCOS_CODE : opco?.code || workspaceCode || '').toLowerCase()

  useEffect(() => {
    if (!open) return undefined
    const onDocMouseDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    inputRef.current?.focus()
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [open])

  useEffect(() => { setActive(0) }, [query, open])

  if (!shouldShowSwitcher({ enabled, opcos, isOpcoAdmin })) return null

  const label = allOpcos ? 'All OpCos' : opco?.name || workspaceCode || 'Select'
  const pill = allOpcos ? 'ALL' : opco?.code || workspaceCode || '?'

  const close = () => {
    setOpen(false)
    setQuery('')
    btnRef.current?.focus()
  }

  const choose = (entry) => {
    if (!entry) return
    if (entry.code.toLowerCase() === currentCode) { close(); return }
    if (!confirmSwitch()) return
    setOpen(false)
    switchTo(entry.code)
  }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => (entries.length ? (i + 1) % entries.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (entries.length ? (i - 1 + entries.length) % entries.length : 0))
    } else if (e.key === 'Home') {
      e.preventDefault()
      setActive(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setActive(Math.max(entries.length - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      choose(entries[active])
    }
  }

  const optionId = (i) => `${listId}-opt-${i}`

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            setOpen(true)
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Workspace: ${label}`}
        title={`Workspace: ${label}`}
        className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-2 sm:px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <Building2 className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <span className="hidden sm:inline text-gray-400 dark:text-gray-500">Workspace:</span>
        <span className="hidden sm:inline max-w-[12rem] truncate font-medium">{label}</span>
        <span className="sm:hidden rounded bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide">{pill}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 max-w-[calc(100vw-2rem)] origin-top-right animate-fade-in bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-1 ring-black/5 border border-gray-200 dark:border-gray-700 overflow-hidden z-[60]">
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search OpCos"
                aria-label="Search OpCos"
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-activedescendant={entries.length ? optionId(active) : undefined}
                className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 pl-8 pr-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              />
            </div>
          </div>
          <ul id={listId} role="listbox" aria-label="Workspaces" className="max-h-72 overflow-y-auto p-1.5">
            {entries.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">No matching OpCos</li>
            )}
            {entries.map((entry, i) => {
              const selected = entry.code.toLowerCase() === currentCode
              return (
                <li
                  key={entry.code}
                  id={optionId(i)}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(entry)}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm cursor-pointer ${
                    i === active ? 'bg-gray-100 dark:bg-gray-700' : ''
                  } ${entry.all ? 'border-b border-gray-100 dark:border-gray-700 mb-1' : ''}`}
                >
                  {entry.all
                    ? <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    : <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-gray-900 dark:text-gray-100">{entry.name}</span>
                    {!entry.all && (
                      <span className="block text-[11px] text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                        {entry.code}{entry.role === 'manager' ? ' · Manager' : ''}
                      </span>
                    )}
                  </span>
                  {selected && <Check className="h-4 w-4 text-teal-600 dark:text-teal-400 flex-shrink-0" aria-hidden="true" />}
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
